/**
 * Shopify API Utilities
 * Type-safe wrappers for Shopify Ajax API
 */

import type {
  ShopifyCart,
  ShopifyCartResponse,
  CartUpdatePayload,
  CartAddPayload,
  ShopifyProduct,
} from '../types/shopify';

export class ShopifyAPI {
  private static baseUrl = window.Shopify?.routes?.root || '/';

  /**
   * Fetch current cart state
   */
  static async getCart(): Promise<ShopifyCartResponse> {
    const response = await fetch(`${this.baseUrl}cart.js`);
    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Add items to cart
   */
  static async addToCart(payload: CartAddPayload): Promise<ShopifyCartResponse> {
    const response = await fetch(`${this.baseUrl}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }

    return response.json();
  }

  /**
   * Update cart quantities
   */
  static async updateCart(payload: CartUpdatePayload): Promise<ShopifyCartResponse> {
    const response = await fetch(`${this.baseUrl}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to update cart');
    }

    return response.json();
  }

  /**
   * Change line item quantity
   */
  static async changeLineItem(
    line: number,
    quantity: number
  ): Promise<ShopifyCartResponse> {
    const response = await fetch(`${this.baseUrl}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });

    if (!response.ok) {
      throw new Error('Failed to change line item');
    }

    return response.json();
  }

  /**
   * Clear entire cart
   */
  static async clearCart(): Promise<ShopifyCartResponse> {
    const response = await fetch(`${this.baseUrl}cart/clear.js`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Failed to clear cart');
    }

    return response.json();
  }

  /**
   * Fetch product JSON
   */
  static async getProduct(handle: string): Promise<ShopifyProduct> {
    const response = await fetch(`${this.baseUrl}products/${handle}.js`);
    if (!response.ok) {
      throw new Error(`Product fetch failed: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Fetch multiple products by handles
   */
  static async getProducts(handles: string[]): Promise<ShopifyProduct[]> {
    const promises = handles.map((handle) => this.getProduct(handle));
    return Promise.all(promises);
  }

  /**
   * Get product recommendations
   */
  static async getRecommendations(
    productId: number,
    limit = 4
  ): Promise<ShopifyProduct[]> {
    const response = await fetch(
      `${this.baseUrl}recommendations/products.json?product_id=${productId}&limit=${limit}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch recommendations');
    }

    const data = await response.json();
    return data.products || [];
  }
}
